import { useTranslation } from "react-i18next";
import { formatEur } from "@/lib/plans";
import type { AppliedPromo } from "../promo";

export interface ReviewDetails {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
}

interface Props {
  line: { title: string; credits: string; priceCents: number };
  promo: AppliedPromo | null;
  details: ReviewDetails;
  onEdit: () => void;
}

/**
 * The confirmation step: everything the customer is about to order, read-only,
 * before "Order now" sends them to pay.
 */
export default function OrderReview({ line, promo, details, onEdit }: Props) {
  const { t } = useTranslation();
  const editButton = (
    <button
      type="button"
      onClick={onEdit}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:underline cursor-pointer"
    >
      <i className="ri-edit-line"></i>
      {t("checkout.review.edit")}
    </button>
  );
  const address = [
    details.addressLine1,
    details.addressLine2,
    [details.postalCode, details.city].filter(Boolean).join(" "),
    t(`checkout.countries.${details.country}`, { defaultValue: details.country }),
  ].filter(Boolean);

  return (
    <section className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-lg text-foreground-950">{t("checkout.review.title")}</h2>
          <p className="mt-1 text-sm text-foreground-600">{t("checkout.review.intro")}</p>
        </div>
        {editButton}
      </div>

      <dl className="mt-6 divide-y divide-background-200/70 text-sm">
        <div className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr]">
          <dt className="text-foreground-500">{t("checkout.review.plan")}</dt>
          <dd className="text-foreground-950">
            <span className="font-semibold">{line.title}</span>
            <span className="block text-foreground-600">{line.credits}</span>
          </dd>
        </div>
        {promo && (
          <div className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr]">
            <dt className="text-foreground-500">{t("checkout.promo.label")}</dt>
            <dd className="text-foreground-950">
              <span className="font-semibold">{promo.code}</span>{" "}
              <span className="text-foreground-600">
                · {formatEur(line.priceCents)} − {formatEur(promo.discountCents)}
              </span>
            </dd>
          </div>
        )}
        <div className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr]">
          <dt className="text-foreground-500">{t("checkout.review.total")}</dt>
          <dd className="font-semibold text-foreground-950">
            {formatEur(promo ? promo.totalCents : line.priceCents)}{" "}
            <span className="font-normal text-foreground-600">{t("checkout.review.totalNote")}</span>
          </dd>
        </div>
        <div className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr]">
          <dt className="text-foreground-500">{t("checkout.review.contact")}</dt>
          <dd className="text-foreground-950 break-words">
            {details.fullName}
            <span className="block text-foreground-600">{details.email}</span>
            <span className="block text-foreground-600">{details.phone}</span>
          </dd>
        </div>
        <div className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr]">
          <dt className="text-foreground-500">{t("checkout.review.billing")}</dt>
          <dd className="text-foreground-950 break-words">
            {details.company && <span className="block">{details.company}</span>}
            {address.map((part) => (
              <span key={part} className="block">
                {part}
              </span>
            ))}
          </dd>
        </div>
      </dl>

      <p className="mt-4 rounded-lg bg-primary-50 px-4 py-3 text-xs leading-relaxed text-primary-800">
        <i className="ri-information-line mr-1"></i>
        {t("checkout.review.notice")}
      </p>
    </section>
  );
}
